import React, { useCallback, useEffect, useState } from 'react';
import { ServerCrash, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { subscribeDolibarr, checkDolibarr } from '../../lib/dolibarrStatus';
import { fmtTime } from '../../lib/easerApi';

const CHECK_DEGRADED_MS = 45000; // pendant la panne : on guette le retour
const CHECK_OK_MS = 5 * 60000;   // en temps normal : contrôle léger

/**
 * Mode dégradé : bandeau clair affiché à l'Easer quand Dolibarr ne répond plus.
 * Les écrans gardent leurs dernières données ; les pointages et saisies partent en file d'attente.
 * Surveille le retour de Dolibarr et prévient (toast + évènement `easer:dolibarr-restored`).
 */
export function DolibarrStatusBanner() {
  const [st, setSt] = useState({ degraded: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribeDolibarr(setSt), []);

  useEffect(() => {
    const onRestored = () => toast.success('Dolibarr est de nouveau disponible : données actualisées et envois en attente transmis.', { id: 'dolibarr-restored', duration: 7000 });
    window.addEventListener('easer:dolibarr-restored', onRestored);
    return () => window.removeEventListener('easer:dolibarr-restored', onRestored);
  }, []);

  useEffect(() => {
    checkDolibarr();
    const timer = setInterval(() => checkDolibarr(), st.degraded ? CHECK_DEGRADED_MS : CHECK_OK_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') checkDolibarr(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', checkDolibarr);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('online', checkDolibarr); };
  }, [st.degraded]);

  const retry = useCallback(async () => {
    setBusy(true);
    try {
      const next = await checkDolibarr();
      if (next.degraded) toast.info('Dolibarr ne répond toujours pas, nouvel essai automatique dans moins d\'une minute.');
    } finally {
      setBusy(false);
    }
  }, []);

  if (!st.degraded) return null;

  return (
    <div className="rounded-xl border border-[hsl(var(--brand-apricot))]/50 bg-[hsl(var(--brand-apricot))]/10 p-3 text-sm mb-4" role="alert" data-testid="dolibarr-degraded-banner">
      <div className="flex items-start gap-2">
        <ServerCrash className="h-4 w-4 mt-0.5 text-[hsl(var(--brand-apricot))] flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold" data-testid="dolibarr-degraded-title">
            Dolibarr est momentanément indisponible{st.downSince ? ` depuis ${fmtTime(st.downSince)}` : ''}{st.simulated ? ' (test de l\'agence)' : ''}
          </p>
          <p className="text-muted-foreground mt-0.5" data-testid="dolibarr-degraded-text">
            Mode dégradé : vos plannings sont affichés tels qu'au dernier chargement. Vos pointages, comptes-rendus et saisies sont gardés sur le téléphone et seront transmis automatiquement dès son retour.
            {st.lastSuccess ? ` Dernière réponse de Dolibarr à ${fmtTime(st.lastSuccess)}.` : ''}
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-8 flex-shrink-0 bg-card" onClick={retry} disabled={busy} data-testid="dolibarr-degraded-retry">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Réessayer
        </Button>
      </div>
    </div>
  );
}
